import { cva, type VariantProps } from 'class-variance-authority'
import { ChevronDown } from 'lucide-react'
import * as React from 'react'

import { cn } from '../lib/cn'

// TODO: migrate hardcoded hex palette to CSS theme variables (see .claude/rules/ui.md).
// Ported as-is from apps/crm-fr to keep visual parity during ui-kit migration.
const badgeVariants = cva(
  'inline-flex items-center rounded-full border font-base transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        primary:
          'border-transparent bg-[#F0F9FF] text-[#036AA3] hover:bg-[#F0F9FF]/80 font-medium dark:bg-[#043047] dark:text-[#D8F3FF] dark:hover:bg-[#043047]/80',
        secondary:
          'border-transparent bg-[#E8E8E8] text-[#344054] hover:bg-[#E8E8E8]/80 font-medium dark:bg-[#1F2937] dark:text-[#E6E6E6] dark:hover:bg-[#1F2937]/80',
        neutral:
          'border-transparent bg-[#faf5ff] text-[#7e22ce] hover:bg-[#faf5ff]/80 font-medium dark:bg-[#2A1340] dark:text-[#EBD7FF] dark:hover:bg-[#2A1340]/80',
        warning:
          'border-transparent bg-[#fff7ed] text-[#C43209] hover:bg-[#fff7ed]/80 font-medium dark:bg-[#3A1F0C] dark:text-[#FFEBD3] dark:hover:bg-[#3A1F0C]/80',
        success:
          'border-transparent bg-[#ECFEF3] text-[#047A48] hover:bg-[#ECFEF3]/80 font-medium dark:bg-[#012A1B] dark:text-[#D9FFF0] dark:hover:bg-[#012A1B]/80',
        error:
          'border-transparent bg-[#FAEFF0] text-[#B12319] hover:bg-[#FAEFF0]/80 font-medium dark:bg-[#3B0B0E] dark:text-[#FFD7DA] dark:hover:bg-[#3B0B0E]/80',
        info: 'border-transparent bg-[#E0F2FE] text-[#0369A1] hover:bg-[#E0F2FE]/80 font-medium dark:bg-[#053241] dark:text-[#CDEFFF] dark:hover:bg-[#053241]/80',
        highlight:
          'border-transparent bg-[#FEF9C3] text-[#92400E] hover:bg-[#FEF9C3]/80 font-medium dark:bg-[#3B2C0A] dark:text-[#FFF6C2] dark:hover:bg-[#3B2C0A]/80',
        archived:
          'border-transparent bg-[#FDF2F8] text-[#9D174D] hover:bg-[#FDF2F8]/80 font-medium dark:bg-[#330D1B] dark:text-[#FFD9EE] dark:hover:bg-[#330D1B]/80',
        paused:
          'border-transparent bg-[#FEE2E2] text-[#991B1B] hover:bg-[#FEE2E2]/80 font-medium dark:bg-[#391616] dark:text-[#FFD6D6] dark:hover:bg-[#391616]/80',
        processing:
          'border-transparent bg-[#EEF2FF] text-[#4338CA] hover:bg-[#EEF2FF]/80 font-medium dark:bg-[#0B1540] dark:text-[#DCE5FF] dark:hover:bg-[#0B1540]/80',
        blocked:
          'border-transparent bg-[#FEF2F2] text-[#B91C1C] hover:bg-[#FEF2F2]/80 font-medium dark:bg-[#3A1010] dark:text-[#FFD6D6] dark:hover:bg-[#3A1010]/80',
        fresh:
          'border-transparent bg-[#D9F6EE] text-[#00695C] hover:bg-[#D9F6EE]/80 font-medium dark:bg-[#03382E] dark:text-[#CFF9E9] dark:hover:bg-[#03382E]/80',
        destructive:
          'border-transparent bg-[#FFE5E5] text-[#E11D48] hover:bg-[#FFE5E5]/80 font-medium dark:bg-[#3B0B10] dark:text-[#FFB3C0] dark:hover:bg-[#3B0B10]/80',
        outline: 'text-[#0C0C1A] bg-transparent border border-[#667085] dark:text-[#E6EEF5] dark:border-[#3F4752]',
        ghost: 'bg-transparent text-[#667085] border-0 !px-0.5 dark:text-[#A6B0C4]',
        prime:
          'border-transparent text-prime font-medium bg-[color-mix(in_oklch,var(--prime)_12%,transparent)] hover:bg-[color-mix(in_oklch,var(--prime)_18%,transparent)]',
      },
    },
    defaultVariants: {
      variant: 'primary',
    },
  },
)

const badgeSize = cva('', {
  variants: {
    size: {
      default: 'px-2.5 py-0.5 min-h-[24px] text-xs',
      big: 'px-3 py-1.5 min-h-[28px] rounded-2xl text-sm',
      tag: 'px-2 py-0.5 min-h-[20px] text-xs',
    },
  },
  defaultVariants: {
    size: 'default',
  },
})

const badgeShape = cva('', {
  variants: {
    shape: {
      default: 'rounded-full',
      tag: 'rounded-sm',
    },
  },
  defaultVariants: {
    shape: 'default',
  },
})

const dotVariants = cva('rounded-full text-sm mr-2', {
  variants: {
    variant: {
      primary: 'bg-[#3b82f6]',
      outline: 'bg-[#667085]',
      secondary: 'bg-[#344054]',
      destructive: 'bg-red-700',
      warning: 'bg-[#f97316]',
      success: 'bg-[#34c759]',
      ghost: 'bg-slate-50',
      neutral: 'bg-[#a855f7]',
      required: 'bg-[#a855f7]',
      error: 'bg-[#ED4337]',
      highlight: 'bg-[#92400E]',
      archived: 'bg-[#9D174D]',
      paused: 'bg-[#991B1B]',
      info: 'bg-[#0369A1]',
      processing: 'bg-[#4338CA]',
      blocked: 'bg-[#B91C1C]',
      fresh: 'bg-[#00695C]',
      prime: 'bg-prime',
    },
    size: {
      default: 'w-1.5 h-1.5 min-w-1.5 min-h-1.5',
      small: 'w-1 h-1 min-w-1 min-h-1',
    },
  },
  defaultVariants: {
    variant: 'primary',
    size: 'default',
  },
})

export type BadgeVariant = VariantProps<typeof badgeVariants>['variant']

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants>, VariantProps<typeof badgeSize> {
  hasDot?: boolean
  hasOutsideDot?: boolean
  /** Dot color, independent of `variant` — e.g. an `outline` badge with a `success` dot. Defaults to `variant`. */
  dotVariant?: BadgeVariant
  Icon?: React.ElementType | null
  /** Trailing chevron inside the pill — marks the badge as a dropdown trigger. */
  hasChevron?: boolean
  count?: string | number
  shape?: VariantProps<typeof badgeShape>['shape']
  wrapperClassName?: string
}

interface DotProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof dotVariants> {}

const Dot = ({ variant, size }: DotProps) => <div className={cn(dotVariants({ variant, size }))} />

function Badge({
  className,
  wrapperClassName,
  variant,
  hasDot = false,
  hasOutsideDot = false,
  dotVariant,
  Icon = null,
  hasChevron = false,
  children,
  size = 'default',
  count,
  shape = 'default',
  ...props
}: BadgeProps) {
  return (
    <div className={cn('relative flex items-center whitespace-nowrap', wrapperClassName)}>
      {hasOutsideDot && <Dot variant={dotVariant ?? variant} size='small' />}

      <div
        className={cn(
          badgeVariants({ variant }),
          badgeSize({ size }),
          badgeShape({ shape }),
          (Icon || hasChevron) && 'flex items-center gap-1',
          className,
        )}
        {...props}
      >
        {hasDot && <Dot variant={dotVariant ?? variant} />}
        {Icon && <Icon height={16} width={16} strokeWidth={1.75} />}
        {children}
        {hasChevron && <ChevronDown aria-hidden className='size-3.5 shrink-0 opacity-70' />}
      </div>

      {count !== undefined && (
        <span className='absolute -top-2 -right-2 bg-destructive text-background text-xs px-2 py-0.5 rounded-full shadow-sm'>
          {count}
        </span>
      )}
    </div>
  )
}

export { Badge, badgeVariants, Dot }
